import { useMemo, useState } from "react";

import {
  useAddSelectedCareer,
  useDeleteSelectedCareer,
  useSelectedCareers,
} from "@features/career-compare/api";
import { useCareerPlan } from "@features/career-details/api/use-career-plan";
import { useStartOver } from "@pages/journey-map/api/use-start-over";
import { useUpdateUserJourney } from "@pages/journey-map/api/use-update-user-journey";
import { useUserJourney } from "@pages/journey-map/api/use-user-journey";

export const useUserSelectedCareers = () => {
  const [openStartOverConfirmation, setOpenStartOverConfirmation] =
    useState(false);

  const { data: selectedCareers, isFetching: fetchingSelectedCareers } =
    useSelectedCareers();

  const {
    mutateAsync: deleteSelectedCareer,
    isPending: isDeletingSelectedCareer,
  } = useDeleteSelectedCareer();

  const { mutateAsync: addSelectedCareer, isPending: addingSelectedCareer } =
    useAddSelectedCareer();

  const { data: userJourney } = useUserJourney();

  const { mutateAsync: updateUserJourney, isPending: isUpdatingUserJourney } =
    useUpdateUserJourney();

  const { startOver, isStartingOver } = useStartOver();

  const { data: careerPlan } = useCareerPlan();

  const removingSelectedCareer = useMemo(() => {
    return isDeletingSelectedCareer || isUpdatingUserJourney || isStartingOver;
  }, [isDeletingSelectedCareer, isUpdatingUserJourney]);

  const addCareer = async (career) => {
    await addSelectedCareer({ career_ids: [career.id] });
  };

  const removeCareer = async (career) => {
    if (careerPlan?.id && careerPlan.careerId === career.id) {
      setOpenStartOverConfirmation({
        onConfirm: async () => {
          await startOver();
          await removeSelectedCareer(career);
          setOpenStartOverConfirmation(undefined);
        },
      });
    } else {
      await removeSelectedCareer(career);
    }
  };

  const selectCareer = async (career) => {
    // Set current career or remove it if it's already selected
    await updateCurrentJourneyCareer(career);
  };

  const onStartOverConfirmation = async () => {
    await openStartOverConfirmation.onConfirm?.();
    setOpenStartOverConfirmation(undefined);
  };

  const updateCurrentJourneyCareer = async (career) => {
    const careerId =
      userJourney?.currentJourneyCareer?.id === career.id ? 0 : career.id;
    await updateUserJourney({ careerId });
  };

  const removeSelectedCareer = async (career) => {
    if (career.id === userJourney?.currentJourneyCareer?.id) {
      await updateCurrentJourneyCareer(career);
    }

    await deleteSelectedCareer({ career_ids: [career.id] });
  };

  return {
    selectedCareers,
    addCareer,
    removeCareer,
    selectCareer,
    removingSelectedCareer,
    addingSelectedCareer,
    fetchingSelectedCareers,
    openStartOverConfirmation,
    onStartOverCancel: () => setOpenStartOverConfirmation(undefined),
    onStartOverConfirmation,
  };
};
